import React, { useState, useRef } from 'react';
import { FaCloudUploadAlt, FaTrash, FaSpinner, FaCheckCircle, FaImage } from 'react-icons/fa';
import { uploadImageToS3 } from '../../aws/contentService_lambda';

const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'image/svg+xml'];
const MAX_SIZE_MB = 5;

const ImageUpload = ({ value, onChange, folder = 'images', theme, label = 'Image' }) => {
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState('');
    const [dragOver, setDragOver] = useState(false);
    const fileInputRef = useRef(null);

    const handleFile = async (file) => {
        setError('');

        if (!ALLOWED_TYPES.includes(file.type)) {
            setError('Only JPG, PNG, WebP, GIF, and SVG files are allowed.');
            return;
        }

        if (file.size > MAX_SIZE_MB * 1024 * 1024) {
            setError(`File size must be under ${MAX_SIZE_MB}MB.`);
            return;
        }

        setUploading(true);
        try {
            const fileUrl = await uploadImageToS3(file, folder);
            onChange(fileUrl);
        } catch (err) {
            console.error('Upload error:', err);
            setError('Upload failed. You can paste a URL instead.');
        } finally {
            setUploading(false);
        }
    };

    const handleFileSelect = (e) => {
        const file = e.target.files[0];
        if (file) handleFile(file);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setDragOver(false);
        const file = e.dataTransfer.files[0];
        if (file) handleFile(file);
    };

    const handleRemove = () => {
        onChange('');
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    return (
        <div>
            <label className={`block text-sm font-semibold mb-2 ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
            }`}>{label}</label>

            {/* Preview */}
            {value && (
                <div className="relative mb-3 group">
                    <img
                        src={value}
                        alt="Preview"
                        className={`w-full h-40 object-contain rounded-lg border-2 ${
                            theme === 'dark' ? 'border-gray-600 bg-gray-700' : 'border-gray-200 bg-gray-50'
                        }`}
                    />
                    <div className="absolute top-2 right-2 flex gap-2">
                        <span className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-semibold ${
                            theme === 'dark' ? 'bg-green-800 text-green-300' : 'bg-green-100 text-green-700'
                        }`}>
                            <FaCheckCircle /> Uploaded
                        </span>
                        <button
                            type="button"
                            onClick={handleRemove}
                            className="p-1.5 rounded bg-red-600 text-white hover:bg-red-700 transition-colors"
                        >
                            <FaTrash className="text-xs" />
                        </button>
                    </div>
                </div>
            )}

            {/* Upload area */}
            {!value && (
                <div
                    onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={handleDrop}
                    onClick={() => !uploading && fileInputRef.current?.click()}
                    className={`flex flex-col items-center justify-center p-6 rounded-lg border-2 border-dashed cursor-pointer transition-all duration-300 ${
                        uploading ? 'cursor-wait' : ''
                    } ${
                        dragOver
                            ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                            : theme === 'dark'
                                ? 'border-gray-600 hover:border-blue-500 bg-gray-700/50 hover:bg-gray-700'
                                : 'border-gray-300 hover:border-blue-500 bg-gray-50 hover:bg-blue-50'
                    }`}
                >
                    {uploading ? (
                        <>
                            <FaSpinner className="text-2xl text-blue-500 animate-spin mb-2" />
                            <p className={`text-sm font-medium ${
                                theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
                            }`}>Uploading to S3...</p>
                        </>
                    ) : (
                        <>
                            <FaCloudUploadAlt className={`text-3xl mb-2 ${
                                theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                            }`} />
                            <p className={`text-sm font-medium ${
                                theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
                            }`}>
                                Drop image here or <span className="text-blue-500 underline">browse</span>
                            </p>
                            <p className={`text-xs mt-1 ${
                                theme === 'dark' ? 'text-gray-500' : 'text-gray-400'
                            }`}>
                                JPG, PNG, WebP, GIF, SVG (max {MAX_SIZE_MB}MB)
                            </p>
                        </>
                    )}
                </div>
            )}

            <input
                ref={fileInputRef}
                type="file"
                accept={ALLOWED_TYPES.join(',')}
                onChange={handleFileSelect}
                className="hidden"
            />

            {/* Fallback: manual URL input */}
            <div className="mt-2">
                <div className={`flex items-center gap-2 text-xs ${
                    theme === 'dark' ? 'text-gray-500' : 'text-gray-400'
                }`}>
                    <FaImage /> or paste URL directly:
                </div>
                <input
                    type="url"
                    value={value || ''}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder="https://example.com/image.png"
                    className={`w-full mt-1 px-3 py-2 text-sm rounded-lg border-2 outline-none transition-colors ${
                        theme === 'dark'
                            ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-500'
                            : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
                    }`}
                />
            </div>

            {error && (
                <p className="text-red-500 text-xs mt-2">{error}</p>
            )}
        </div>
    );
};

export default ImageUpload;
